import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ListedAgo from "@/components/ListedAgo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShoppingBag, Eye, ArrowLeft, Trash2, Share2 } from "lucide-react";
import { TID } from "@/constants/tid";
import PaymentMethodPicker from "@/components/PaymentMethodPicker";
import { ReportListingDialog } from "@/components/ReportListingDialog";
import LogisticsNotice from "@/components/LogisticsNotice";
import { track } from "@/lib/analytics";
import { ListenButton } from "@/components/ListenButton";
import { useI18n } from "@/i18n/I18nContext";
import { SellerTrustBadge } from "@/components/SellerTrustBadge";
import { fmtPrice } from "@/lib/currency";
import { CurrencyFlip, useConvertedPrice } from "@/components/CurrencyFlip";
import { EnquiryBox } from "@/components/EnquiryBox";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1615788189819-bee84874da4b?auto=format&fit=crop&w=1200&q=80";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const { t, lang } = useI18n();
  const convertedBuy = useConvertedPrice(p?.price || 0, p?.currency || "USD");
  const [tr, setTr] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    if (!p) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "product-jsonld";
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: p.title,
      description: p.description,
      image: p.image_url?.startsWith("http") ? p.image_url : `${process.env.REACT_APP_BACKEND_URL}${p.image_url || ""}`,
      brand: { "@type": "Brand", name: p.seller_name || "VISMARKS" },
      offers: {
        "@type": "Offer",
        price: (p.price || 0).toFixed(2),
        priceCurrency: p.currency || "USD",
        availability: p.stock > 0 && p.status === "active"
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        url: `${window.location.origin}/product/${p.id}`,
      },
    });
    document.head.appendChild(el);
    const prevTitle = document.title;
    document.title = `${p.title} — ${fmtPrice(p.price, p.currency)} | VISMARKS`;
    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") || "";
    if (metaDesc) metaDesc.setAttribute("content", (p.description || "").slice(0, 155));
    let canon = document.getElementById("product-canonical");
    if (!canon) {
      canon = document.createElement("link");
      canon.rel = "canonical";
      canon.id = "product-canonical";
      document.head.appendChild(canon);
    }
    canon.href = `${window.location.origin}/product/${p.id}`;
    return () => {
      document.getElementById("product-jsonld")?.remove();
      document.getElementById("product-canonical")?.remove();
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, [p]);
  const [err, setErr] = useState("");
  const [rewards, setRewards] = useState([]);
  const [applyReward, setApplyReward] = useState(false);

  useEffect(() => {
    try {
      const sref = new URLSearchParams(window.location.search).get("sref");
      if (sref && /^[a-z0-9]{1,16}$/i.test(sref)) localStorage.setItem("vsk_sref", sref);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user && user !== false && user.role === "buyer") {
      api.get("/rewards/mine").then(({ data }) => {
        setRewards((data.rewards || []).filter((r) => r.status === "active"));
      }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setP(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  useEffect(() => {
    setTr(null);
    setShowOriginal(false);
    if (!p || lang === "en") return;
    api.post("/i18n/product", { product_id: p.id, lang }, { timeout: 60000 })
      .then(({ data }) => setTr(data))
      .catch(() => {});
  }, [p, lang]);

  const dispTitle = tr && !showOriginal ? tr.title : p?.title;
  const dispDescription = tr && !showOriginal ? tr.description : p?.description;

  const remove = async () => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      nav("/seller");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (err) {
    return (
      <div className="max-w-3xl mx-auto p-10">
        <div className="text-[#FF3B30]">{err}</div>
        <Link to="/discover" className="mt-4 inline-flex items-center gap-2 text-white">
          <ArrowLeft className="h-4 w-4" /> Back to discover
        </Link>
      </div>
    );
  }

  if (!p) return <div className="p-10 text-neutral-500">Loading…</div>;

  const canDelete = user && user !== false && (user.role === "admin" || user.id === p.seller_id);
  const isOwner = user && user !== false && user.id === p.seller_id;
  const canBuy = user && user !== false && !isOwner && p.status === "active";

  return (
    <div data-testid={TID.productDetail} className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/discover" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm">
        <ArrowLeft className="h-4 w-4" /> Back to discover
      </Link>

      <div className="mt-6 grid lg:grid-cols-2 gap-10">
        <div className="relative rounded-xl overflow-hidden border border-[#27272A] bg-[#121212] aspect-[4/3] lg:aspect-square" data-testid="product-image-frame">
          <img src={p.image_url || FALLBACK_IMG} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <img
            src={p.image_url || FALLBACK_IMG}
            alt={p.title}
            className="relative w-full h-full object-contain"
            onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
          />
        </div>

        <div>
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 rounded-full text-[10px] uppercase tracking-widest bg-white/[0.03] border border-white/10 text-neutral-300">
              {p.category}
            </span>
            {p.founding_seller && (
              <span data-testid="founding-seller-badge" className="px-2 py-1 rounded-full text-[10px] uppercase tracking-widest bg-[#FF9F0A]/10 border border-[#FF9F0A]/40 text-[#FF9F0A] font-bold">
                ★ Founding seller
              </span>
            )}
            <Link to={`/store/${p.seller_id}`} className="text-xs text-neutral-400 hover:text-white underline underline-offset-2" data-testid="product-store-link">by {p.seller_name || "seller"} · visit Stage</Link>
          </div>
          <h1 className="mt-3 font-black tracking-tight text-4xl leading-tight">{dispTitle}</h1>
          <SellerTrustBadge sellerId={p.seller_id} />
          {tr && (
            <button
              data-testid="translation-toggle"
              onClick={() => setShowOriginal((s) => !s)}
              className="mt-2 text-[11px] text-neutral-500 hover:text-white underline"
            >
              {showOriginal ? t("pd_translated", "Auto-translated") : t("pd_original", "View original")}
            </button>
          )}

          <button
            onClick={async () => {
              let shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/product/${id}`;
              if (user && user !== false) {
                try {
                  const { data } = await api.get("/rewards/share-code");
                  shareUrl += `?sref=${data.share_code}`;
                } catch { /* share without ref */ }
              }
              const payload = { title: `${p.title} — ${fmtPrice(p.price, p.currency)} on VISMARKS`, url: shareUrl };
              if (navigator.share) {
                try { await navigator.share(payload); return; } catch { /* cancelled */ }
              }
              try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Share link copied — when a friend buys through it, you earn a reward code");
              } catch {
                toast.error("Could not copy the link");
              }
            }}
            data-testid="share-product-btn"
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white transition-colors"
          >
            <Share2 className="h-3.5 w-3.5" /> Share this product
          </button>
          <button
            onClick={async () => {
              let shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/product/${id}`;
              if (user && user !== false) {
                try {
                  const { data } = await api.get("/rewards/share-code");
                  shareUrl += `?sref=${data.share_code}`;
                } catch { /* share without ref */ }
              }
              const text = `🔥 ${p.title} — ${fmtPrice(p.price, p.currency)} on VISMARKS. Check it out: ${shareUrl}`;
              window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
            }}
            data-testid="whatsapp-share-btn"
            className="mt-3 ml-4 inline-flex items-center gap-1.5 text-xs text-[#25D366] hover:text-[#4ce287] transition-colors font-semibold"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            {t("pd_share", "Share on WhatsApp")}
          </button>
          <ListenButton
            className="mt-3 ml-4"
            text={`${p.title}. Priced at ${p.price.toFixed(2)} ${p.currency || "dollars"}. ${(p.ai_summary || p.description || "").slice(0, 300)}`}
          />

          <div className="mt-4 flex items-center gap-6">
            <div className="text-3xl font-mono tabular-nums font-bold">{fmtPrice(p.price, p.currency)}</div>
            <CurrencyFlip amount={p.price} currency={p.currency || "USD"} />
            <div className="text-xs uppercase tracking-widest text-neutral-500">
              <div>Availability</div>
              <div className="mt-1"><ListedAgo publishedAt={p.published_at} /></div>
            </div>
          </div>

          {p.ai_summary && (
            <div className="mt-6 rounded-md border border-[#007AFF]/30 bg-[#007AFF]/5 p-4 text-sm text-neutral-200">
              <div className="text-[10px] uppercase tracking-widest text-[#007AFF] mb-1">AI summary</div>
              {p.ai_summary}
            </div>
          )}

          <p className="mt-6 text-neutral-300 leading-relaxed whitespace-pre-line">{dispDescription}</p>

          {p.tags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {p.tags.map((t) => (
                <span key={t} className="text-xs px-2 py-1 rounded-full bg-white/[0.03] border border-white/10 text-neutral-400">
                  #{t}
                </span>
              ))}
            </div>
          )}

          <div className="mt-8 flex items-center gap-6 text-sm text-neutral-400 font-mono">
            <span className="inline-flex items-center gap-2"><Eye className="h-4 w-4" /> {p.views} views</span>
            <span className="inline-flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> {p.purchases} bought</span>
            <span>{t("pd_stock", "Stock")}: {p.stock}</span>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {p.status === "active" && p.seller_can_sell === false && !isOwner && (!user || user === false || user.role !== "admin") ? (
              <EnquiryBox productId={id} productTitle={p.title} user={user} />
            ) : canBuy && p.seller_payouts_connected === false ? (
              <div className="w-full space-y-3" data-testid={TID.productBuy}>
                <div data-testid="product-held-notice" className="rounded-md border border-[#007AFF]/30 bg-[#007AFF]/[0.06] p-3 text-xs text-neutral-300">
                  <span className="text-white font-semibold">Pay securely by card.</span> VISMARKS holds the payment and releases it to the seller once they connect their payout account — your buyer protection applies as usual.
                </div>
                <PaymentMethodPicker purpose="product_order" productId={id} quantity={1} amountUsd={p.price} providers={{ stripe: true }}
                  extraBody={{ sref: localStorage.getItem("vsk_sref") || undefined }} />
                <LogisticsNotice variant="buyer" />
              </div>
            ) : canBuy && (
              <div className="w-full space-y-3" data-testid={TID.productBuy}>
                {rewards.length > 0 && (
                  <label data-testid="apply-reward-toggle" className="flex items-center gap-2.5 rounded-md border border-[#FF9F0A]/40 bg-[#FF9F0A]/[0.06] px-3 py-2.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applyReward}
                      onChange={(e) => setApplyReward(e.target.checked)}
                      className="h-4 w-4 accent-[#FF9F0A]"
                    />
                    <span className="text-neutral-300">
                      🎁 Apply reward <span className="font-mono text-white">{rewards[0].code}</span>{" "}
                      <span className="text-[#FF9F0A]">(save up to ${rewards[0].amount_usd.toFixed(2)})</span>
                    </span>
                  </label>
                )}
                <PaymentMethodPicker
                  purpose="product_order"
                  productId={id}
                  quantity={1}
                  amountUsd={p.price}
                  providers={p.seller_channels || { stripe: true, paystack: true, flutterwave: true }}
                  extraBody={{
                    sref: localStorage.getItem("vsk_sref") || undefined,
                    discount_code: applyReward && rewards.length > 0 ? rewards[0].code : undefined,
                  }}
                />
                <LogisticsNotice variant="buyer" />
              </div>
            )}
            {isOwner && (
              <div data-testid="owner-listing-panel" className="w-full rounded-md border border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.06] p-4 text-sm text-neutral-300">
                <div className="text-[10px] uppercase tracking-widest text-[#FF9F0A] mb-1">Your listing</div>
                <span className="text-white font-semibold">You can't buy your own product.</span>{" "}
                {p.seller_can_sell === false
                  ? <>Right now buyers see <span className="text-white">"message the seller"</span> here instead of Buy. To switch on Buy, finish your seller setup:</>
                  : <>Buyers see the <span className="text-white">Buy now</span> button here and pay by card{p.seller_payouts_connected === false ? " — VISMARKS holds each payment until you connect your payout account" : ""}.</>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to="/seller" className="h-8 px-3 inline-flex items-center rounded-full border border-white/20 text-white text-xs font-bold" data-testid="owner-edit-listing">Manage listing →</Link>
                  {(p.seller_sell_blockers || []).includes("subscription") && <Link to="/billing" className="h-8 px-3 inline-flex items-center rounded-full bg-[#FF3B30] text-white text-xs font-bold" data-testid="blocker-plan">Activate seller plan →</Link>}
                  {(p.seller_sell_blockers || []).includes("verification") && <Link to="/earnings" className="h-8 px-3 inline-flex items-center rounded-full border border-white/20 text-white text-xs font-bold" data-testid="blocker-kyc">Verify ID (Earnings page) →</Link>}
                  {p.seller_can_sell !== false && p.seller_payouts_connected === false && <Link to="/earnings" className="h-8 px-3 inline-flex items-center rounded-full border border-white/20 text-white text-xs font-bold" data-testid="owner-connect-payouts">Connect payouts to get paid →</Link>}
                  {(p.seller_sell_blockers || []).includes("banned") && <span className="text-xs text-[#FF3B30]">Account restricted — contact support.</span>}
                </div>
              </div>
            )}
            {(!user || user === false) && p.status === "active" && p.seller_can_sell !== false && (
              <div className="w-full space-y-2" data-testid="product-guest-buy">
                <Button
                  data-testid="guest-buy-btn"
                  onClick={() => { track("buy_intent", { product_id: id }); nav(`/register?next=/product/${id}`); }}
                  className="w-full h-12 rounded-full bg-[#FF3B30] hover:bg-[#e0342a] text-white font-bold text-base"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" /> Buy now — {fmtPrice(p.price, p.currency)}{convertedBuy ? ` (≈ ${convertedBuy})` : ""}
                </Button>
                <p className="text-xs text-neutral-500 text-center">
                  Free 20-second account, then straight back here to complete your order.{" "}
                  <Link to={`/login?next=/product/${id}`} className="text-[#007AFF] hover:underline" data-testid="guest-buy-login-link">
                    Already have one? Log in
                  </Link>
                </p>
              </div>
            )}
            {p.status !== "active" && (
              <span className="text-xs uppercase tracking-widest text-[#FF3B30]">
                {p.status === "expired" ? "No longer available" : p.status === "paused" ? "Paused by the seller — check back soon" : "Sold out"}
              </span>
            )}
            {canDelete && (
              <Button
                data-testid={TID.productDelete}
                variant="outline"
                onClick={remove}
                className="rounded-full border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] bg-transparent h-11 px-4"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
          </div>

          <div className="mt-8 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="seller-info-block">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Sold by</div>
            <div className="mt-1.5 text-sm text-white font-semibold">
              {p.seller_info?.company || p.seller_info?.name || p.seller_name || "Independent seller"}
            </div>
            <div className="mt-0.5 text-xs text-neutral-500">
              {p.seller_info?.name && p.seller_info?.company ? `${p.seller_info.name} · ` : ""}
              {p.seller_info?.member_since ? `Seller on VISMARKS since ${new Date(p.seller_info.member_since).toLocaleDateString(undefined, { month: "short", year: "numeric" })}` : "Verified VISMARKS seller"}
            </div>
            <p className="mt-2 text-[11px] text-neutral-500 leading-relaxed">
              This seller handles shipping, fulfilment and returns for this product.
            </p>
            <div className="mt-3">
              <ReportListingDialog productId={id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
